import { SupportPermissions } from "@redotech/account-common/permissions";
import type { SupportTicketNotification } from "@redotech/effect-api-shared/models/support-ticket/support-ticket-notification";
import { useAsyncDebounce } from "@redotech/react-util/debounce";
import { useHandler } from "@redotech/react-util/hook";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { useSubscribe } from "@redotech/redo-merchant-app-common/stream/stream-provider";
import { useStreamingEnabled } from "@redotech/redo-merchant-app-common/streaming-enabled";
import { useSupportTicketQueryScope } from "@redotech/redo-merchant-app-common/support/use-support-ticket-query-scope";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useSupportTicketCountsForViews } from "@redotech/redo-merchant-support-app/v2/hooks/use-support-ticket-counts";
import { DefaultView } from "@redotech/redo-support-v2/query/default-views";
import {
  buildDefaultSupportTicketCountViews,
  type SupportTicketCountView,
} from "@redotech/redo-support-v2/query/support-ticket-count-query";
import { supportTicketCountsScopedQueryKey } from "@redotech/redo-support-v2/query/support-ticket-query-keys";
import { sinkPromise } from "@redotech/util/promise";
import { useQueryClient } from "@tanstack/react-query";
import { useContext, useMemo } from "react";

const OPEN_COUNT_VIEWS: SupportTicketCountView[] =
  buildDefaultSupportTicketCountViews(null).filter(
    (view) => view.viewKey === DefaultView.OPEN,
  );

// Counts read OpenSearch, which reindexes after the write, so a refresh on the
// event itself can read the old count. Waiting also caps refreshes on busy teams.
const OPEN_COUNT_REFRESH_DELAY_MS = 30_000;

function changesOpenCount(event: SupportTicketNotification): boolean {
  if (event._tag === "supportTicketCreated") {
    return true;
  }
  if (event._tag !== "supportTicketUpdated") {
    return false;
  }
  switch (event.updateType) {
    case "status_changed":
    case "assigned":
    case "tag_added":
    case "tag_removed":
      return true;
    case "priority_changed":
    case "read":
      return false;
    default: {
      const unhandled: never = event.updateType;
      return unhandled;
    }
  }
}

export function useHasAnyOpenConversations(): boolean {
  const team = useContext(TeamContext);
  const streamingEnabled = useStreamingEnabled();
  const hasSupportPermission = useHasPermission(
    SupportPermissions.VIEW_SUPPORT,
  );
  const queryClient = useQueryClient();
  const queryScope = useSupportTicketQueryScope();

  const supportEnabled = team?.settings.support?.enabled;
  const shouldCheck =
    !!streamingEnabled && !!supportEnabled && hasSupportPermission;

  const counts = useSupportTicketCountsForViews(
    useMemo(() => (shouldCheck ? OPEN_COUNT_VIEWS : []), [shouldCheck]),
    // The nav is on every page, so a failed refresh shouldn't toast.
    { showErrorToast: false },
  );

  const refreshCounts = useHandler(() =>
    queryClient.invalidateQueries({
      queryKey: supportTicketCountsScopedQueryKey(queryScope),
    }),
  );
  const scheduleRefresh = useAsyncDebounce(
    refreshCounts,
    OPEN_COUNT_REFRESH_DELAY_MS,
    { maxWait: OPEN_COUNT_REFRESH_DELAY_MS },
  );

  useSubscribe({
    channel: "support-ticket-notifications",
    enabled: shouldCheck,
    onEvent: (event) => {
      if (changesOpenCount(event)) {
        sinkPromise(scheduleRefresh());
      }
    },
    // The stream doesn't replay events missed while it was disconnected.
    onSubscribed: () => {
      sinkPromise(scheduleRefresh());
    },
  });

  return (counts.get(DefaultView.OPEN) ?? 0) > 0;
}
